import React, { useCallback, useEffect, useState } from 'react';
import { Breadcrumbs } from './Breadcrumbs';
import { BookOpenCheckIcon, UploadIcon, FileIcon } from 'lucide-react';
// Debounce function for auto-saving
const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};
export const NotesInterface = ({
  classes,
  activeClass,
  activeNote,
  onUpdateNote,
  onStartReview
}) => {
  const [noteContent, setNoteContent] = useState('');
  const [isNewNote, setIsNewNote] = useState(false);
  const [showUploadPrompt, setShowUploadPrompt] = useState(false);
  // Find active class and note
  const classObj = classes.find(c => c.id === activeClass);
  const noteObj = classObj?.notes.find(n => n.id === activeNote);
  // Set up auto-save
  const debouncedSave = useCallback(debounce(content => {
    if (activeClass && activeNote) {
      onUpdateNote(activeClass, activeNote, content);
    }
  }, 1000), [activeClass, activeNote, onUpdateNote]);
  // Handle content changes
  const handleContentChange = e => {
    const content = e.target.value;
    setNoteContent(content);
    debouncedSave(content);
  };
  // Check if this is a new note
  useEffect(() => {
    if (noteObj && !noteObj.content) {
      setIsNewNote(true);
      setShowUploadPrompt(true);
    } else {
      setIsNewNote(false);
      setShowUploadPrompt(false);
    }
  }, [noteObj]);
  // Reset note content when switching notes
  useEffect(() => {
    setNoteContent(noteObj?.content || '');
  }, [noteObj]);
  // Handle file upload
  const handleFileUpload = e => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = event => {
        const content = event.target.result;
        setNoteContent(content);
        debouncedSave(content);
        setShowUploadPrompt(false);
      };
      reader.readAsText(file);
    }
  };
  // Update the review button in all instances
  const ReviewButton = () => <button onClick={onStartReview} className="bg-black hover:bg-gray-800 text-white px-4 py-2 rounded-lg transition-colors" data-id="element-51">
      Review
    </button>;
  // If no class or note is selected
  if (!activeClass || !activeNote) {
    return <div className="flex items-center justify-center h-full text-gray-500" data-id="element-52">
        <div className="text-center" data-id="element-53">
          <BookOpenCheckIcon size={48} className="mx-auto mb-4 text-gray-300" data-id="element-54" />
          <h2 className="text-xl font-medium mb-2" data-id="element-55">No note selected</h2>
          <p data-id="element-56">Select a class and note from the sidebar to get started</p>
        </div>
      </div>;
  }
  // New note welcome screen
  if (isNewNote && showUploadPrompt) {
    return <div className="h-full flex flex-col" data-id="element-57">
        <div className="bg-white border-b border-gray-200 p-4 flex justify-between items-center" data-id="element-58">
          <div data-id="element-59">
            <Breadcrumbs className={classObj?.name} note={noteObj?.title} data-id="element-60" />
            <h1 className="text-2xl font-bold text-gray-800" data-id="element-61">
              {noteObj?.title}
            </h1>
          </div>
          <ReviewButton data-id="element-62" />
        </div>
        <div className="flex-1 flex items-center justify-center p-8" data-id="element-63">
          <div className="text-center max-w-md" data-id="element-64">
            <h2 className="text-2xl font-bold mb-6" data-id="element-65">Start Your Note</h2>
            <div className="space-y-6" data-id="element-66">
              <button onClick={() => setShowUploadPrompt(false)} className="w-full p-6 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors" data-id="element-67">
                <h3 className="text-lg font-semibold mb-2" data-id="element-68">Start Blank Note</h3>
                <p className="text-gray-600" data-id="element-69">
                  Begin with a clean slate and start typing your notes
                </p>
              </button>
              <div className="relative" data-id="element-70">
                <input type="file" onChange={handleFileUpload} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" accept=".txt,.doc,.docx,.pdf" data-id="element-71" />
                <div className="w-full p-6 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors" data-id="element-72">
                  <h3 className="text-lg font-semibold mb-2" data-id="element-73">Upload File</h3>
                  <p className="text-gray-600" data-id="element-74">
                    Import content from an existing document
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>;
  }
  // Main note editor
  return <div className="h-full flex flex-col" data-id="element-75">
      <div className="bg-white border-b border-gray-200 p-4 flex justify-between items-center" data-id="element-76">
        <div data-id="element-77">
          <Breadcrumbs className={classObj?.name} note={noteObj?.title} data-id="element-78" />
          <h1 className="text-2xl font-bold text-gray-800" data-id="element-79">{noteObj?.title}</h1>
        </div>
        <ReviewButton data-id="element-80" />
      </div>
      <div className="flex-1 overflow-auto bg-white" data-id="element-81">
        <textarea className="w-full h-full p-8 focus:outline-none resize-none" placeholder="Start typing your notes here..." value={noteContent} onChange={handleContentChange} data-id="element-82" />
      </div>
    </div>;
};